import {
  components,
} from '@/generated/qmulus/openapi.ts';
import {
  TComponentValue,
} from '@/types/openapi-type.ts';
import {
  EUnitOfMeasurement,
} from '@/types/unit-of-measurement.ts';

export enum ESimulatorSimulationPartShape {
  ROUND_STEEL = 0,
  HEXAGON_OR_OCTAGON_STEEL = 1,
  SQUARE_OR_FLAT_STEEL = 2,
  SOLID_STEEL = 3,
  RING = 4,
  CYL_HOLLOW_BODY_LESS_80 = 5,
  CYL_HOLLOW_BODY_LESS_200 = 6,
  CYL_HOLLOW_BODY_GREATER_200 = 7,
  HOLLOW_BODY_CLOSED = 8,
}

export const simulatorSimulationPartShapeMap: Record<components['schemas']['SimulatorSimulationPartShape'], keyof typeof ESimulatorSimulationPartShape> = {
  0: 'ROUND_STEEL',
  1: 'HEXAGON_OR_OCTAGON_STEEL',
  2: 'SQUARE_OR_FLAT_STEEL',
  3: 'SOLID_STEEL',
  4: 'RING',
  5: 'CYL_HOLLOW_BODY_LESS_80',
  6: 'CYL_HOLLOW_BODY_LESS_200',
  7: 'CYL_HOLLOW_BODY_GREATER_200',
  8: 'HOLLOW_BODY_CLOSED',
};

export enum ESimulatorRecipeType {
  CARBURIZING = 0,
  CARBONITRIDING = 1,
  LOW_PRESSURE_CARBURIZING = 2,
  NITRIDING = 3,
  NITROCARBURIZING = 4,
}

export const simulatorRecipeTypeMap: Record<components['schemas']['SimulatorRecipeType'], keyof typeof ESimulatorRecipeType> = {
  0: 'CARBURIZING',
  1: 'CARBONITRIDING',
  2: 'LOW_PRESSURE_CARBURIZING',
  3: 'NITRIDING',
  4: 'NITROCARBURIZING',
};

// AMS2759/10 Compound Layer Class
export enum ESimulatorAMS275910CompoundLayerClass {
  CLASS_0 = 0,
  CLASS_1 = 1,
  CLASS_2 = 2,
}

// Will be used for mapping, when schema is updated
export const simulatorAMS275910CompoundLayerClassMap: Record<components['schemas']['SimulatorAMS275910CompoundLayerClass'], keyof typeof ESimulatorAMS275910CompoundLayerClass> = {
  0: 'CLASS_0',
  1: 'CLASS_1',
  2: 'CLASS_2',
};

// AMS2759/10 Steel Class
export enum ESimulatorAMS275910SteelClass {
  NITRALLOY_135M = 0,
  NITRALLOY_EZ = 1,
  NITRALLOY_N = 2,
  _4140_4340 = 3,
  DC_D6AC = 4,
  H11 = 5,
  STAINLESS = 6,
  CARBON = 7,
}

export const simulatorAMS275910SteelClassMap: Record<components['schemas']['SimulatorAMS275910SteelClass'], keyof typeof ESimulatorAMS275910SteelClass> = {
  0: 'NITRALLOY_135M',
  1: 'NITRALLOY_EZ',
  2: 'NITRALLOY_N',
  3: '_4140_4340',
  4: 'DC_D6AC',
  5: 'H11',
  6: 'STAINLESS',
  7: 'CARBON',
};

// AMS2759/12 Porosity Class
export enum ESimulatorAMS275912PorosityClass {
  CLASS_1 = 1,
  CLASS_2 = 2,
}

export const simulatorAMS275912PorosityClassMap: Record<components['schemas']['SimulatorAMS275912PorosityClass'], keyof typeof ESimulatorAMS275912PorosityClass> = {
  1: 'CLASS_1',
  2: 'CLASS_2',
};

// AMS2759/12 Steel Class
export enum ESimulatorAMS275912SteelClass {
  GROUP_0 = 1,
  GROUP_1 = 2,
  GROUP_2 = 3,
}

// Will be used for mapping, when schema is updated
export const simulatorAMS275912SteelClassMap: Record<components['schemas']['SimulatorAMS275912SteelClass'], keyof typeof ESimulatorAMS275912SteelClass> = {
  1: 'GROUP_0',
  2: 'GROUP_1',
  3: 'GROUP_2',
};

export enum ESimulatorPreTreatmentType {
  NORMALIZED = 0,
  QUENCHED = 1,
  TEMPERED = 2,
}

// Will be used for mapping, when schema is updated
export const simulatorPreTreatmentTypeMap: Record<components['schemas']['SimulatorPreTreatmentType'], keyof typeof ESimulatorPreTreatmentType> = {
  0: 'NORMALIZED',
  1: 'QUENCHED',
  2: 'TEMPERED',
};

export enum ESimulatorRecipeSimulationValuesType {
  AVG_VAL = 0,
  COMP_VAL = 1,
  USER_VAL = 2,
}

export const simulatorRecipeSimulationValuesTypeMap: Record<components['schemas']['SimulatorRecipeSimulationValuesType'], keyof typeof ESimulatorRecipeSimulationValuesType> = {
  0: 'AVG_VAL',
  1: 'COMP_VAL',
  2: 'USER_VAL',
};

export const simulatorRecipeSimulationValuesTypeToMaterialPropertySpectrum: Record<ESimulatorRecipeSimulationValuesType, (keyof components['schemas']['MaterialPropertySpectrum']) | undefined> = {
  [ESimulatorRecipeSimulationValuesType.AVG_VAL]: 'avg',
  [ESimulatorRecipeSimulationValuesType.COMP_VAL]: 'comp',
  [ESimulatorRecipeSimulationValuesType.USER_VAL]: undefined,
};

export enum ESimulatorRecipeSetpointType {
  RUN_TIME = 0,
  TEMPERATURE = 1,
  // Austenite (Carburizing, Carbonitriding)
  CARBURIZING_DEPTH_RELATIVE = 10,
  CARBURIZING_DEPTH_ABSOLUTE = 11,
  CARBON_POTENTIAL_CP = 12,
  SURFACE_CARBON = 13,
  AUTO_BOOST_C = 14,
  SOOT_LIMIT = 15,
  NITROGEN_POTENTIAL_NP = 16,
  // Ferrite (Nitrogen, Nitrocarburizing)
  NITRIDING_DEPTH_RELATIVE = 20,
  NITRIDING_DEPTH_ABSOLUTE = 21,
  NITRIDING_POTENTIAL_KN = 22,
  SURFACE_NITROGEN = 23,
  AUTO_BOOST_N = 24,
  CARBURIZING_POTENTIAL_KC_B = 25,
  CARBURIZING_POTENTIAL_KC_W = 26,
  // Atmosphere
  TOTAL_GAS_FLOW = 30,
  TOTAL_GAS_FLOW_AUTO = 31,
  CO_CONTENT = 32,
  H2_CONTENT = 33,
  N2_CONTENT = 34,
  NH3_CONTENT = 35,
  COX_CONTENT = 36,
  C2H2_CONTENT = 37,
  C3H8_CONTENT = 38,
  // other
  WAIT_RELATIVE = 50,
}

export type TSimulatorRecipeSetpointType = TComponentValue<'SimulatorRecipeSetpoint'>['simulatorSetpointMeaningId'];

// Will be used for mapping, when schema is updated
export const SimulatorRecipeSetpointTypeMap: Record<TSimulatorRecipeSetpointType, keyof typeof ESimulatorRecipeSetpointType> = {
  RUN_TIME: 'RUN_TIME',
  TEMPERATURE: 'TEMPERATURE',
  // Austenite (Carburizing, Carbonitriding)
  CARBURIZING_DEPTH_RELATIVE: 'CARBURIZING_DEPTH_RELATIVE',
  CARBURIZING_DEPTH_ABSOLUTE: 'CARBURIZING_DEPTH_ABSOLUTE',
  CARBON_POTENTIAL_CP: 'CARBON_POTENTIAL_CP',
  SURFACE_CARBON: 'SURFACE_CARBON',
  AUTO_BOOST_C: 'AUTO_BOOST_C',
  SOOT_LIMIT: 'SOOT_LIMIT',
  NITROGEN_POTENTIAL_NP: 'NITROGEN_POTENTIAL_NP',
  // Ferrite (Nitrogen, Nitrocarburizing)
  NITRIDING_DEPTH_RELATIVE: 'NITRIDING_DEPTH_RELATIVE',
  NITRIDING_DEPTH_ABSOLUTE: 'NITRIDING_DEPTH_ABSOLUTE',
  NITRIDING_POTENTIAL_KN: 'NITRIDING_POTENTIAL_KN',
  SURFACE_NITROGEN: 'SURFACE_NITROGEN',
  AUTO_BOOST_N: 'AUTO_BOOST_N',
  CARBURIZING_POTENTIAL_KC_B: 'CARBURIZING_POTENTIAL_KC_B',
  CARBURIZING_POTENTIAL_KC_W: 'CARBURIZING_POTENTIAL_KC_W',
  // Atmosphere
  TOTAL_GAS_FLOW: 'TOTAL_GAS_FLOW',
  TOTAL_GAS_FLOW_AUTO: 'TOTAL_GAS_FLOW_AUTO',
  CO_CONTENT: 'CO_CONTENT',
  H2_CONTENT: 'H2_CONTENT',
  N2_CONTENT: 'N2_CONTENT',
  NH3_CONTENT: 'NH3_CONTENT',
  COX_CONTENT: 'COX_CONTENT',
  C2H2_CONTENT: 'C2H2_CONTENT',
  C3H8_CONTENT: 'C3H8_CONTENT',
  WAIT_RELATIVE: 'WAIT_RELATIVE',
};

export const SimulatorRecipeSetpointTypeDefaultUnitMap: Record<TSimulatorRecipeSetpointType, EUnitOfMeasurement> = {
  RUN_TIME: EUnitOfMeasurement.SECOND,
  TEMPERATURE: EUnitOfMeasurement.DEGREES_CELSIUS,
  // Carburizing (austenite)
  CARBURIZING_DEPTH_RELATIVE: EUnitOfMeasurement.PERCENT,
  CARBURIZING_DEPTH_ABSOLUTE: EUnitOfMeasurement.MILLIMETRE,
  CARBON_POTENTIAL_CP: EUnitOfMeasurement.PERCENT_CARBON,
  SURFACE_CARBON: EUnitOfMeasurement.PERCENT_CARBON,
  AUTO_BOOST_C: EUnitOfMeasurement.PERCENT,
  SOOT_LIMIT: EUnitOfMeasurement.PERCENT,
  NITROGEN_POTENTIAL_NP: EUnitOfMeasurement.PERCENT_NITROGEN,
  // Nitriding (ferrit)
  NITRIDING_DEPTH_RELATIVE: EUnitOfMeasurement.PERCENT,
  NITRIDING_DEPTH_ABSOLUTE: EUnitOfMeasurement.MILLIMETRE,
  NITRIDING_POTENTIAL_KN: EUnitOfMeasurement.UNIT_LESS,
  SURFACE_NITROGEN: EUnitOfMeasurement.PERCENT_NITROGEN,
  AUTO_BOOST_N: EUnitOfMeasurement.PERCENT,
  CARBURIZING_POTENTIAL_KC_B: EUnitOfMeasurement.UNIT_LESS,
  CARBURIZING_POTENTIAL_KC_W: EUnitOfMeasurement.UNIT_LESS,
  // Atmosphere
  TOTAL_GAS_FLOW: EUnitOfMeasurement.NORMAL_LITRE_PER_SQUARE_METERS_PER_HOUR,
  TOTAL_GAS_FLOW_AUTO: EUnitOfMeasurement.NORMAL_LITRE_PER_SQUARE_METERS_PER_HOUR,
  CO_CONTENT: EUnitOfMeasurement.PERCENT,
  H2_CONTENT: EUnitOfMeasurement.PERCENT,
  N2_CONTENT: EUnitOfMeasurement.PERCENT,
  NH3_CONTENT: EUnitOfMeasurement.PERCENT,
  COX_CONTENT: EUnitOfMeasurement.PERCENT,
  C2H2_CONTENT: EUnitOfMeasurement.PERCENT,
  C3H8_CONTENT: EUnitOfMeasurement.PERCENT,
  WAIT_RELATIVE: EUnitOfMeasurement.UNIT_LESS,
};

export const SimulatorRecipeSetpointTypeNumberToNameMap: Record<ESimulatorRecipeSetpointType, TSimulatorRecipeSetpointType> = {
  [ESimulatorRecipeSetpointType.RUN_TIME]: 'RUN_TIME',
  [ESimulatorRecipeSetpointType.TEMPERATURE]: 'TEMPERATURE',
  // Carburizing
  [ESimulatorRecipeSetpointType.CARBURIZING_DEPTH_RELATIVE]: 'CARBURIZING_DEPTH_RELATIVE',
  [ESimulatorRecipeSetpointType.CARBURIZING_DEPTH_ABSOLUTE]: 'CARBURIZING_DEPTH_ABSOLUTE',
  [ESimulatorRecipeSetpointType.CARBON_POTENTIAL_CP]: 'CARBON_POTENTIAL_CP',
  [ESimulatorRecipeSetpointType.SURFACE_CARBON]: 'SURFACE_CARBON',
  [ESimulatorRecipeSetpointType.AUTO_BOOST_C]: 'AUTO_BOOST_C',
  [ESimulatorRecipeSetpointType.SOOT_LIMIT]: 'SOOT_LIMIT',
  [ESimulatorRecipeSetpointType.NITROGEN_POTENTIAL_NP]: 'NITROGEN_POTENTIAL_NP',
  // Nitriding
  [ESimulatorRecipeSetpointType.NITRIDING_DEPTH_RELATIVE]: 'NITRIDING_DEPTH_RELATIVE',
  [ESimulatorRecipeSetpointType.NITRIDING_DEPTH_ABSOLUTE]: 'NITRIDING_DEPTH_ABSOLUTE',
  [ESimulatorRecipeSetpointType.NITRIDING_POTENTIAL_KN]: 'NITRIDING_POTENTIAL_KN',
  [ESimulatorRecipeSetpointType.SURFACE_NITROGEN]: 'SURFACE_NITROGEN',
  [ESimulatorRecipeSetpointType.AUTO_BOOST_N]: 'AUTO_BOOST_N',
  [ESimulatorRecipeSetpointType.CARBURIZING_POTENTIAL_KC_B]: 'CARBURIZING_POTENTIAL_KC_B',
  [ESimulatorRecipeSetpointType.CARBURIZING_POTENTIAL_KC_W]: 'CARBURIZING_POTENTIAL_KC_W',
  // Atmosphere
  [ESimulatorRecipeSetpointType.TOTAL_GAS_FLOW]: 'TOTAL_GAS_FLOW',
  [ESimulatorRecipeSetpointType.TOTAL_GAS_FLOW_AUTO]: 'TOTAL_GAS_FLOW_AUTO',
  [ESimulatorRecipeSetpointType.CO_CONTENT]: 'CO_CONTENT',
  [ESimulatorRecipeSetpointType.H2_CONTENT]: 'H2_CONTENT',
  [ESimulatorRecipeSetpointType.N2_CONTENT]: 'N2_CONTENT',
  [ESimulatorRecipeSetpointType.NH3_CONTENT]: 'NH3_CONTENT',
  [ESimulatorRecipeSetpointType.COX_CONTENT]: 'COX_CONTENT',
  [ESimulatorRecipeSetpointType.C2H2_CONTENT]: 'C2H2_CONTENT',
  [ESimulatorRecipeSetpointType.C3H8_CONTENT]: 'C3H8_CONTENT',
  // other
  [ESimulatorRecipeSetpointType.WAIT_RELATIVE]: 'WAIT_RELATIVE',
};

export const SimulatorGasContentSetpoints: TSimulatorRecipeSetpointType[] = [
  'CO_CONTENT',
  'H2_CONTENT',
  'N2_CONTENT',
  'NH3_CONTENT',
  'COX_CONTENT',
  'C2H2_CONTENT',
  'C3H8_CONTENT',
];

export const SimulatorRecipeEndControlConditionSymbolMap: Record<TComponentValue<'SimulatorRecipeSetpointEndControl'>['controlType'], string> = {
  NONE: '--',
  LESS_OR_EQUAL: '≤',
  GREATER_OR_EQUAL: '≥',
};

export const SimulatorRecipeControlConditionSymbolMap: Record<TComponentValue<'SimulatorRecipeSetpointControl'>['controlType'], string> = {
  NONE: '--',
  EQUAL: '=',
  INCREASE: '+=',
  DECREASE: '-=',
};

export const SimulatorSetpointControlValueFactorMap: Record<TComponentValue<'SimulatorRecipeSetpointControl'>['controlType'], number> = {
  NONE: 1,
  EQUAL: 1,
  INCREASE: 60, // BE expected x/sec we ask user for x/min for better UX
  DECREASE: 60, // BE expected x/sec we ask user for x/min for better UX
};
